import React, { useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ChevronLeft, 
  Loader2,
} from 'lucide-react';
import { projectAPI, createQueryKey } from '../services/api';
import ProjectView from '../components/ProjectView';

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch project details - include refreshKey to force refetch when needed
  const { data: projectResponse, isLoading: projectLoading, error: projectError, refetch: refetchProject } = useQuery({
    queryKey: createQueryKey.project(projectId),
    queryFn: async () => {
      console.log('[ProjectDetailPage] Fetching project:', projectId);
      // Add cache-busting timestamp to ensure fresh data
      const response = await projectAPI.getById(projectId);
      // Handle both response structures: response.data (direct) or response.data.project (wrapped)
      const project = response.data?.project || response.data;
      console.log('[ProjectDetailPage] Project fetched:', {
        id: project?.id,
        name: project?.name,
        status: project?.status,
        statusType: typeof project?.status,
        settings: project?.settings,
        responseStructure: response.data?.project ? 'wrapped' : 'direct',
        fullResponse: response
      });
      return response;
    },
    enabled: !!projectId && projectId !== 'undefined' && projectId !== 'null',
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: false, // Don't auto-refetch, but allow manual refetch
    staleTime: 0, // Always consider data stale to force refetch
    cacheTime: 0, // Don't cache - always fetch fresh
    // Don't retry on 404 - project doesn't exist
    retry: (failureCount, error) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
  
  // Listen for navigation events and force refetch if needed
  useEffect(() => {
    const handleFocus = () => {
      // Refetch when window gains focus (user might have updated project in another tab)
      refetchProject();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetchProject]);

  // Guard against invalid ID
  useEffect(() => {
    const isProjectDetailRoute = location.pathname.match(/^\/projects\/[^/]+$/);
    
    // Only redirect if we are actually trying to view a project detail page
    // AND the ID is missing. This prevents redirects during exit animations 
    // when navigating to pages like /discover
    if (isProjectDetailRoute && (!projectId || projectId === 'undefined' || projectId === 'null')) {
      console.warn('Invalid project ID detected:', projectId, 'Redirecting to projects page');
      navigate('/projects', { replace: true });
    }
  }, [projectId, navigate, location.pathname]);

  // Refetch project when status might have changed (listen for focus/visibility)
  useEffect(() => {
    if (!projectId || projectId === 'undefined' || projectId === 'null') return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[ProjectDetailPage] Page visible, refetching project');
        refetchProject();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [projectId, refetchProject]);

  // Handle response structure - backend returns ProjectResponse directly
  // axios wraps it in response.data, so it could be response.data (project object) or response.data.project
  const project = projectResponse?.data?.project || projectResponse?.data;

  if (!projectId || projectId === 'undefined' || projectId === 'null') return null;

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (projectError || !project) {
    console.error('[ProjectDetailPage] Project fetch error:', {
      error: projectError,
      response: projectResponse,
      projectId,
      status: projectError?.response?.status,
      detail: projectError?.response?.data?.detail,
      responseData: projectResponse?.data
    });
    return (
      <div className="flex flex-col h-[calc(100vh-64px)]">
        <div className="text-center pt-20">
          <h1 className="text-2xl font-bold text-gray-900">Project not found</h1>
          <Link
            to="/projects"
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Canonical project dashboard experience
  return <ProjectView project={project} projectId={projectId} />;
};

export default ProjectDetailPage;
